import type {LayoutPayload} from '@github-ui/code-view-types'
import {getQueryClient} from '@github-ui/query-client'
import {useIsFetching} from '@github-ui/react-query'
import {useParams} from '@github-ui/react-router'
import type {RefInfo} from '@github-ui/repos-types'
import {useEffect, useMemo} from 'react'

import {codeViewLayoutRoute} from '../routes/code-view-layout-route'

export function useUpdateRefInfoAndPath(refInfo: RefInfo, path: string) {
  const {owner, repo} = useParams()

  // Build the layout query key from route params instead of `useRouteQuery`.
  // `useRouteQuery` to avoid subscribing to the query here.
  const layoutQueryKey = useMemo(
    () => codeViewLayoutRoute.queries.mainQuery.makeQueryKey({pathname: `/${owner}/${repo}/_layout`}),
    [owner, repo],
  )

  // Observe only the layout query's fetch status (not its data) so refInfo and
  // path are re-applied after the query is refetched, without subscribing to the
  // query result or requiring the route to be mounted.
  const layoutIsFetching = useIsFetching({queryKey: layoutQueryKey, exact: true})

  useEffect(() => {
    const queryClient = getQueryClient()
    const layoutData = queryClient.getQueryData<{payload: LayoutPayload}>(layoutQueryKey)

    if (!layoutData) return

    queryClient.setQueryData(layoutQueryKey, (oldData: {payload: LayoutPayload} | undefined) => {
      if (!oldData) return oldData

      return {
        ...oldData,
        payload: {
          ...oldData.payload,
          refInfo: {
            ...oldData.payload.refInfo,
            ...refInfo,
          },
          path,
        },
      }
    })
  }, [refInfo, path, layoutQueryKey, layoutIsFetching])
}
